import { useId, useRef, useState } from "react";
import { useAuth } from "../lib/auth/useAuth";
import { useStartActivity } from "../hooks/useActivities";
import { useUiStore } from "../state/uiStore";
import type { ActivityEntry } from "../state/uiStore";
import { buildEvent, getWorkerUrl } from "../lib/session";
import type { ActivityContextRefs } from "../lib/session";
import {
  buildContextRefs,
  EMPTY_LINKS,
  type ActivityLinks,
} from "../lib/activityLinks";
import { ActivityPicker } from "../components/ActivityPicker";
import { ActivityLinkFields } from "../components/ActivityLinkFields";
import { recordActivityUsage } from "../lib/activityUsage";
import { NewTaskSheet } from "./TasksScreen";
import { useDialog } from "../hooks/useDialog";

interface Props {
  catalog: ActivityEntry[];
}

/** Modale « Démarrer une activité » (design Chronos) : sélection d'une activité
 * du catalogue (chips), note optionnelle, et liaison Projet (PM) + Tâche (TM).
 * Le catalogue est piloté par l'admin — le worker ne fait que sélectionner. */
export function NewActivitySheet({ catalog }: Props) {
  const sheetOpen = useUiStore((s) => s.sheetOpen);
  // Monté à l'ouverture seulement : le montage capture le déclencheur, le
  // démontage lui rend le focus et remet le formulaire à zéro.
  return sheetOpen ? <NewActivityDialog catalog={catalog} /> : null;
}

function NewActivityDialog({ catalog }: Props) {
  const { closeSheet, pushToast } = useUiStore();
  const { user } = useAuth();

  const workerUrl = (() => {
    try {
      return getWorkerUrl(user);
    } catch {
      return "";
    }
  })();

  const startActivity = useStartActivity();

  const [selected, setSelected] = useState<ActivityEntry | null>(null);
  const [note, setNote] = useState("");
  const [links, setLinks] = useState<ActivityLinks>(EMPTY_LINKS);
  const [creatingTask, setCreatingTask] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const titleId = useId();
  useDialog(ref, closeSheet);

  async function handleStart() {
    if (!selected || !workerUrl) return;
    if (selected.requires_worker_note && !note.trim()) {
      pushToast({
        variant: "error",
        title: "Note requise",
        subtitle: "Cette activité nécessite une note.",
      });
      return;
    }
    try {
      const base = await buildEvent(workerUrl, "activity_start", selected.code);
      const trimmedNote = note.trim();
      const refs: ActivityContextRefs = {
        ...(trimmedNote ? { note: trimmedNote } : {}),
        ...buildContextRefs(links),
      };
      const payload = Object.keys(refs).length
        ? { ...base, context_refs: refs as Record<string, unknown> }
        : base;
      await startActivity.mutateAsync(payload);
      recordActivityUsage(selected.code);
      pushToast({
        variant: "info",
        title: "Activité démarrée",
        subtitle: selected.label,
      });
      closeSheet();
    } catch {
      pushToast({
        variant: "error",
        title: "Erreur",
        subtitle: "Impossible de démarrer l'activité.",
      });
    }
  }

  return (
    <>
      <div className="scrim" onClick={closeSheet} />
      <div
        className="activity-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        tabIndex={-1}
        ref={ref}
      >
        <h2 id={titleId}>Démarrer une activité</h2>

        <div className="am-eyebrow">Activité *</div>
        <ActivityPicker
          items={catalog}
          selectedCode={selected?.code ?? null}
          onSelect={(code) =>
            setSelected(catalog.find((a) => a.code === code) ?? null)
          }
        />

        <div className="am-eyebrow">Note</div>
        <input
          className="field"
          placeholder={
            selected?.requires_worker_note
              ? "Note (obligatoire)"
              : "Note (optionnel)"
          }
          value={note}
          onChange={(e) => setNote(e.target.value)}
          maxLength={200}
        />

        <div className="am-eyebrow">
          Lier à un projet, un terrain ou une tâche
          <button
            type="button"
            className="btn-link"
            onClick={() => setCreatingTask(true)}
          >
            + Nouvelle tâche
          </button>
        </div>
        <ActivityLinkFields value={links} onChange={setLinks} />

        <div className="am-actions">
          <button
            className="btn primary"
            disabled={!selected || startActivity.isPending}
            onClick={() => void handleStart()}
          >
            Démarrer
          </button>
          <button className="btn" onClick={closeSheet}>
            Annuler
          </button>
        </div>
      </div>
      {creatingTask && (
        <NewTaskSheet nested onClose={() => setCreatingTask(false)} />
      )}
    </>
  );
}
